import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Leaf, ArrowLeft, ArrowRight, Check, Loader2, X, Pill, AlertTriangle, Search, Sparkles, Phone, Clock, Save, RefreshCw, Lock, Shield, Heart, Brain, Moon, Zap, Activity, Eye, Bone, Scale, Smile } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local storage key for saving assessment
const SAVED_ASSESSMENT_KEY = 'vitala_saved_assessment';
// Key to indicate a guest just completed the quiz and needs to submit after auth
const PENDING_GUEST_QUIZ_KEY = 'vitala_pending_guest_quiz';

export default function QuizPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  // Local state for quiz
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [originalAnswers, setOriginalAnswers] = useState(null);
  const [isRevising, setIsRevising] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [hasSavedAssessment, setHasSavedAssessment] = useState(false);
  const [showSavedPrompt, setShowSavedPrompt] = useState(false);
  // Saved-assessment metadata (questions answered + how long ago) shown in
  // the resume prompt so users know what they're returning to.
  const [savedMeta, setSavedMeta] = useState(null);
  const [showGateScreen, setShowGateScreen] = useState(false);
  
  // Medications state
  const [medications, setMedications] = useState([]);
  const [medicationInput, setMedicationInput] = useState('');
  const [commonMedications, setCommonMedications] = useState([]);
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);
  const [filteredMedications, setFilteredMedications] = useState([]);
  
  // Current supplements state
  const [currentSupplements, setCurrentSupplements] = useState([]);
  const [supplementInput, setSupplementInput] = useState('');
  const [commonSupplements, setCommonSupplements] = useState([]);
  const [showSupplementDropdown, setShowSupplementDropdown] = useState(false);
  const [filteredSupplements, setFilteredSupplements] = useState([]);
  
  // Country search state
  const [countrySearch, setCountrySearch] = useState('');
  
  const hasInitialized = useRef(false);

  // Check for saved assessment on mount
  useEffect(() => {
    const savedData = localStorage.getItem(SAVED_ASSESSMENT_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Check if saved data is less than 7 days old
        const savedTime = new Date(parsed.savedAt);
        const now = new Date();
        const daysDiff = (now - savedTime) / (1000 * 60 * 60 * 24);
        
        if (daysDiff < 7) {
          setHasSavedAssessment(true);
          setShowSavedPrompt(true);
          // Compute friendly metadata once for the resume prompt
          const ms = now.getTime() - savedTime.getTime();
          const mins = Math.floor(ms / 60000);
          let agoLabel;
          if (mins < 1) agoLabel = 'just now';
          else if (mins < 60) agoLabel = `${mins} min ago`;
          else if (mins < 24 * 60) agoLabel = `${Math.floor(mins / 60)}h ago`;
          else agoLabel = `${Math.floor(mins / (60 * 24))}d ago`;
          const answeredCount = Object.keys(parsed.answers || {}).length;
          setSavedMeta({
            agoLabel,
            answered: answeredCount,
            currentStep: parsed.currentStep || 0,
          });
        } else {
          // Clear old saved data
          localStorage.removeItem(SAVED_ASSESSMENT_KEY);
        }
      } catch (e) {
        localStorage.removeItem(SAVED_ASSESSMENT_KEY);
      }
    }
  }, []);

  // Auto-submit pending guest quiz after user authenticates
  useEffect(() => {
    const autoSubmitPendingQuiz = async () => {
      const pendingData = localStorage.getItem(PENDING_GUEST_QUIZ_KEY);
      if (!user || !pendingData) return;
      
      try {
        const parsed = JSON.parse(pendingData);
        const savedTime = new Date(parsed.savedAt);
        const daysDiff = (new Date() - savedTime) / (1000 * 60 * 60 * 24);
        if (daysDiff > 7) {
          localStorage.removeItem(PENDING_GUEST_QUIZ_KEY);
          localStorage.removeItem(SAVED_ASSESSMENT_KEY);
          return;
        }

        setIsSubmitting(true);
        setShowGateScreen(false);
        setProcessingStatus({ step: 1, total: 3, message: 'Processing your assessment...' });

        const formattedAnswers = parsed.answers;
        const medsList = parsed.medications || [];
        const currentSuppsList = parsed.currentSupplements || [];

        const response = await axios.post(`${API}/assessment`, {
          answers: formattedAnswers,
          medications: medsList,
          current_supplements: currentSuppsList
        }, { headers: getAuthHeader() });

        // Clean up
        localStorage.removeItem(PENDING_GUEST_QUIZ_KEY);
        localStorage.removeItem(SAVED_ASSESSMENT_KEY);

        const assessmentId = response.data?.id || response.data?.assessment_id;
        if (assessmentId) {
          toast.success('Your personalized report is ready!');
          navigate(`/results/${assessmentId}`);
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Auto-submit pending quiz error:', err);
        toast.error('Something went wrong. Please try submitting again.');
        // Restore the quiz so user can manually submit
        const parsed = JSON.parse(pendingData);
        if (parsed.rawAnswers) setAnswers(parsed.rawAnswers);
        if (parsed.medications) setMedications(parsed.medications);
        if (parsed.currentSupplements) setCurrentSupplements(parsed.currentSupplements);
        setShowGateScreen(false);
      } finally {
        setIsSubmitting(false);
        setProcessingStatus(null);
      }
    };

    autoSubmitPendingQuiz();
  }, [user, navigate, getAuthHeader]);

  // Function to save assessment to localStorage
  const saveAssessmentLocally = useCallback(() => {
    const dataToSave = {
      answers,
      medications,
      currentSupplements,
      currentStep,
      savedAt: new Date().toISOString(),
      userId: user?.id
    };
    localStorage.setItem(SAVED_ASSESSMENT_KEY, JSON.stringify(dataToSave));
    toast.success('Assessment saved! You can return later to submit it.');
  }, [answers, medications, currentSupplements, currentStep, user?.id]);

  // Function to restore saved assessment
  const restoreSavedAssessment = useCallback(() => {
    const savedData = localStorage.getItem(SAVED_ASSESSMENT_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setAnswers(parsed.answers || {});
        setMedications(parsed.medications || []);
        setCurrentSupplements(parsed.currentSupplements || []);
        setCurrentStep(parsed.currentStep || 0);
        setShowSavedPrompt(false);
        toast.success('Your saved assessment has been restored!');
      } catch (e) {
        toast.error('Could not restore saved assessment');
        localStorage.removeItem(SAVED_ASSESSMENT_KEY);
      }
    }
  }, []);

  // Function to clear saved assessment and start fresh
  const clearSavedAssessment = useCallback(() => {
    localStorage.removeItem(SAVED_ASSESSMENT_KEY);
    setHasSavedAssessment(false);
    setShowSavedPrompt(false);
    setAnswers({});
    setMedications([]);
    setCurrentSupplements([]);
    setCurrentStep(0);
    toast.info('Starting fresh assessment');
  }, []);

  // Filter questions based on gender and conditional logic
  const getFilteredQuestions = useCallback(() => {
    const genderAnswer = answers['gender'];
    
    return questions.filter(q => {
      // Skip menopause and fertility questions for males
      if (genderAnswer === 'Male' && (q.category === 'menopause' || q.category === 'fertility')) {
        return false;
      }
      
      // Handle conditional questions
      if (q.condition) {
        const conditionQuestionId = q.condition.question_id;
        const userAnswer = answers[conditionQuestionId];
        
        // If no answer yet for the condition question, skip this conditional question
        if (!userAnswer) return false;
        
        // Check "contains_any" condition (matches if answer contains ANY of the provided strings)
        if (q.condition.contains_any) {
          const values = q.condition.contains_any;
          if (Array.isArray(userAnswer)) {
            return userAnswer.some(a => values.some(v => a.includes(v)));
          }
          return values.some(v => userAnswer.includes(v));
        }
        
        // Check "contains" condition (for multi-select answers)
        if (q.condition.contains) {
          // userAnswer could be an array (multi-select) or string
          if (Array.isArray(userAnswer)) {
            return userAnswer.some(a => a.includes(q.condition.contains));
          }
          return userAnswer.includes(q.condition.contains);
        }
        
        // Check "equals" condition (for single-select answers)
        if (q.condition.equals) {
          return userAnswer === q.condition.equals;
        }
      }
      
      return true;
    });
  }, [questions, answers]);

  const filteredQuestions = getFilteredQuestions();

  // Calculate steps: filtered questions + supplements step + medications step
  const isSupplementStep = currentStep === filteredQuestions.length && filteredQuestions.length > 0;
  const isMedicationStep = currentStep === filteredQuestions.length + 1 && filteredQuestions.length > 0;
  const totalSteps = filteredQuestions.length + 2; // +1 for supplements, +1 for medications
  const actualProgress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  // Get unique sections/categories from questions for progress tracking
  const quizSections = useMemo(() => {
    const sectionOrder = [
      'demographics', 'lifestyle', 'health_conditions', 'prostate', 'sleep', 'energy', 
      'stress', 'digestion', 'immunity', 'joint', 'skin', 'cognitive', 
      'mood', 'heart', 'weight', 'vision', 'menopause', 'fertility'
    ];
    
    const uniqueCategories = [...new Set(filteredQuestions.map(q => q.category))];
    // Sort by predefined order, with any missing ones at the end
    const sortedCategories = uniqueCategories.sort((a, b) => {
      const indexA = sectionOrder.indexOf(a);
      const indexB = sectionOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    
    // Add supplements and medications as final sections
    return [...sortedCategories, 'supplements', 'medications'];
  }, [filteredQuestions]);

  // Get current section based on current step
  const getCurrentSection = useCallback(() => {
    if (isSupplementStep) return 'supplements';
    if (isMedicationStep) return 'medications';
    if (filteredQuestions[currentStep]) {
      return filteredQuestions[currentStep].category;
    }
    return quizSections[0] || 'demographics';
  }, [currentStep, filteredQuestions, isSupplementStep, isMedicationStep, quizSections]);

  const currentSection = getCurrentSection();

  // Calculate section progress
  const getSectionProgress = useCallback(() => {
    const currentSectionIndex = quizSections.indexOf(currentSection);
    const completedSections = currentSectionIndex;
    const totalSections = quizSections.length;
    return { completedSections, totalSections, currentSectionIndex };
  }, [quizSections, currentSection]);

  // Estimate time remaining (approximately 8 seconds per question on average)
  const estimatedTimeRemaining = useMemo(() => {
    const remainingSteps = totalSteps - currentStep - 1;
    const secondsPerStep = 8;
    const totalSeconds = remainingSteps * secondsPerStep;
    
    if (totalSeconds < 60) return 'Less than 1 min left';
    const minutes = Math.ceil(totalSeconds / 60);
    return `About ${minutes} min${minutes > 1 ? 's' : ''} left`;
  }, [totalSteps, currentStep]);

  // Format section name for display
  const formatSectionName = (section) => {
    const sectionNames = {
      'demographics': 'Basic Info',
      'lifestyle': 'Lifestyle',
      'health_conditions': 'Health Conditions',
      'prostate': 'Prostate Health',
      'sleep': 'Sleep',
      'energy': 'Energy',
      'stress': 'Stress',
      'digestion': 'Digestion',
      'immunity': 'Immunity',
      'joint': 'Joint Health',
      'skin': 'Skin & Hair',
      'cognitive': 'Brain Health',
      'mood': 'Mood',
      'heart': 'Heart Health',
      'weight': 'Weight',
      'vision': 'Vision',
      'menopause': 'Menopause',
      'fertility': 'Fertility',
      'supplements': 'Current Supplements',
      'medications': 'Medications'
    };
    return sectionNames[section] || section.replace('_', ' ');
  };

  // Removed: Auth redirect - quiz is now accessible without login
  // Gate screen is shown after quiz completion for non-authenticated users

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const fetchData = async () => {
      try {
        const [questionsRes, medsRes, suppsRes] = await Promise.all([
          axios.get(`${API}/quiz/questions`),
          axios.get(`${API}/medications`),
          axios.get(`${API}/current-supplements`)
        ]);
        setQuestions(questionsRes.data.questions);
        setCommonMedications(medsRes.data.medications);
        setCommonSupplements(suppsRes.data.supplements);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load questions');
      } finally {
        setLoadingQuestions(false);
      }
    };

    fetchData();
  }, []);

  // Pre-fill from previous assessment if ?prefill=true — runs once user is hydrated
  const prefillAttempted = useRef(false);
  useEffect(() => {
    if (prefillAttempted.current) return;
    if (!user) return;
    if (!questions.length) return; // need question definitions to normalize multi-selects
    const params = new URLSearchParams(window.location.search);
    if (params.get('prefill') !== 'true') return;
    prefillAttempted.current = true;

    (async () => {
      try {
        const prevRes = await axios.get(`${API}/assessment/latest-answers`, { headers: getAuthHeader() });
        if (prevRes.data.has_previous) {
          const prevAnswers = prevRes.data.answers || {};
          // Normalize: older assessments stored multi-select answers as comma-joined strings
          // (e.g. "Thyroid issues, Joint pain"). Coerce them back to arrays NOW so the rest
          // of the UI (checkIfAnswered, "N selected" indicator, Continue gating, edited-badge
          // diff) only ever sees arrays for multi-select questions.
          const multiSelectIds = new Set(
            questions.filter(q => q.type === 'multi_select').map(q => q.id)
          );
          const optionsByQuestion = {};
          questions.filter(q => q.type === 'multi_select').forEach(q => {
            optionsByQuestion[q.id] = q.options || [];
          });
          const normalized = { ...prevAnswers };
          // Build helpers for height/weight legacy-string parsing
          const heightIds = new Set(
            questions.filter(q => q.type === 'height_input').map(q => q.id)
          );
          const weightIds = new Set(
            questions.filter(q => q.type === 'weight_input').map(q => q.id)
          );
          for (const qid of Object.keys(normalized)) {
            const v = normalized[qid];

            // ─── Height legacy parsing ─────────────────────────────
            // Old shape: string like "5'4\"" or "5'4" or "170 cm"
            // New shape: { feet, inches, unit: 'ft-in' } or { cm, unit: 'cm' }
            if (heightIds.has(qid) && typeof v === 'string' && v.length > 0) {
              const cmMatch = v.match(/^\s*(\d+(?:\.\d+)?)\s*cm\s*$/i);
              const ftInMatch = v.match(/^\s*(\d+)\s*'\s*(\d+(?:\.\d+)?)?\s*"?\s*$/);
              if (cmMatch) {
                normalized[qid] = { cm: cmMatch[1], unit: 'cm' };
              } else if (ftInMatch) {
                normalized[qid] = { feet: ftInMatch[1], inches: ftInMatch[2] || '0', unit: 'ft-in' };
              } else {
                normalized[qid] = {}; // leave blank — user will re-enter
              }
              continue;
            }

            // ─── Weight legacy parsing ─────────────────────────────
            // Old shape: string like "145 lbs" or "65 kg"
            // New shape: { value, unit: 'lbs'|'kg' }
            if (weightIds.has(qid) && typeof v === 'string' && v.length > 0) {
              const m = v.match(/^\s*(\d+(?:\.\d+)?)\s*(lbs?|kg|kilograms?|pounds?)?\s*$/i);
              if (m) {
                const rawUnit = (m[2] || 'lbs').toLowerCase();
                const unit = rawUnit.startsWith('k') ? 'kg' : 'lbs';
                normalized[qid] = { value: m[1], unit };
              } else {
                normalized[qid] = {};
              }
              continue;
            }

            if (!multiSelectIds.has(qid)) continue;
            let arr;
            if (Array.isArray(v)) {
              arr = v;
            } else if (typeof v === 'string' && v.length > 0) {
              arr = v.split(',').map(s => s.trim()).filter(Boolean);
            } else {
              arr = [];
            }
            // Filter to ONLY options that are still defined for this question.
            // Drops orphan values from removed/renamed options, preventing the
            // "163 selected" bug where junk inflated the count and broke validation.
            const validSet = new Set(optionsByQuestion[qid] || []);
            const validLower = new Map(
              (optionsByQuestion[qid] || []).map(o => [String(o).toLowerCase(), o])
            );
            const filtered = [];
            const seen = new Set();
            arr.forEach(item => {
              const s = String(item).trim();
              if (!s) return;
              // Match exactly, else case-insensitively
              let canonical = validSet.has(s) ? s : validLower.get(s.toLowerCase());
              if (!canonical) return;
              if (seen.has(canonical)) return;
              seen.add(canonical);
              filtered.push(canonical);
            });
            normalized[qid] = filtered;
          }
          setAnswers(normalized);
          setOriginalAnswers({ ...normalized });
          setIsRevising(true);
          setMedications(prevRes.data.medications || []);
          setCurrentSupplements(prevRes.data.current_supplements || []);
          toast.success("Previous answers loaded — just update what's changed!");
        }
      } catch (e) {
        console.error('Could not load previous answers:', e);
      }
    })();
  }, [user, questions.length]);

  // Scroll to top when question changes
  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Delayed scroll to catch any render lag
    const t1 = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    const t2 = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [currentStep]);

  // Filter medications
  useEffect(() => {
    if (medicationInput.length > 0) {
      const filtered = commonMedications.filter(med => 
        med.name.toLowerCase().includes(medicationInput.toLowerCase()) ||
        med.category.toLowerCase().includes(medicationInput.toLowerCase())
      );
      setFilteredMedications(filtered.slice(0, 8));
      setShowMedicationDropdown(true);
    } else {
      setShowMedicationDropdown(false);
    }
  }, [medicationInput, commonMedications]);

  // Filter supplements
  useEffect(() => {
    if (supplementInput.length > 0) {
      const filtered = commonSupplements.filter(supp => 
        supp.name.toLowerCase().includes(supplementInput.toLowerCase()) ||
        supp.category.toLowerCase().includes(supplementInput.toLowerCase())
      );
      setFilteredSupplements(filtered.slice(0, 8));
      setShowSupplementDropdown(true);
    } else {
      setShowSupplementDropdown(false);
    }
  }, [supplementInput, commonSupplements]);

  const setAnswer = useCallback((questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  }, []);

  const handleOptionSelect = (option) => {
    const currentQuestion = filteredQuestions[currentStep];
    setAnswer(currentQuestion.id, option);
    
    // Auto-advance to next question after selection (with small delay for visual feedback)
    setTimeout(() => {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
      }
    }, 300);
  };

  const handleNext = () => {
    if (!isSupplementStep && !isMedicationStep) {
      const currentQuestion = filteredQuestions[currentStep];
      if (!answers[currentQuestion.id]) {
        toast.error('Please select an answer');
        return;
      }
    }
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Medication handlers
  const addMedication = (medName) => {
    if (medName && !medications.includes(medName)) {
      setMedications([...medications, medName]);
    }
    setMedicationInput('');
    setShowMedicationDropdown(false);
  };

  const removeMedication = (med) => {
    setMedications(medications.filter(m => m !== med));
  };

  const handleAddCustomMedication = () => {
    if (medicationInput.trim()) {
      addMedication(medicationInput.trim());
    }
  };

  // Supplement handlers
  const addSupplement = (suppName) => {
    if (suppName && !currentSupplements.includes(suppName)) {
      setCurrentSupplements([...currentSupplements, suppName]);
    }
    setSupplementInput('');
    setShowSupplementDropdown(false);
  };

  const removeSupplement = (supp) => {
    setCurrentSupplements(currentSupplements.filter(s => s !== supp));
  };

  const handleAddCustomSupplement = () => {
    if (supplementInput.trim()) {
      addSupplement(supplementInput.trim());
    }
  };

  // Handle answers for weight/height inputs
  const handleAnswer = (questionId, answerValue) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerValue
    }));
  };

  const getFormattedAnswers = () => {
    return Object.entries(answers).map(([question_id, answer]) => {
      // Handle weight input - format as "150 lbs" or "68 kg"
      if (typeof answer === 'object' && answer.value !== undefined) {
        return {
          question_id,
          answer: `${answer.value} ${answer.unit}`
        };
      }
      // Handle height input - format as "5'10"" or "178 cm"
      if (typeof answer === 'object' && (answer.feet !== undefined || answer.cm !== undefined)) {
        if (answer.unit === 'cm') {
          return {
            question_id,
            answer: `${answer.cm} cm`
          };
        } else {
          return {
            question_id,
            answer: `${answer.feet || 0}'${answer.inches || 0}"`
          };
        }
      }
      // Handle multi-select - join with commas
      if (Array.isArray(answer)) {
        return {
          question_id,
          answer: answer.join(', ')
        };
      }
      return {
        question_id,
        answer
      };
    });
  };

  // Check if current question is answered (handles weight/height/country/multi-select inputs)
  const checkIfAnswered = (question, answerValue) => {
    if (!question || answerValue === undefined || answerValue === null) return false;
    
    if (question.type === 'weight_input') {
      return answerValue.value && answerValue.value > 0;
    }
    if (question.type === 'height_input') {
      if (answerValue.unit === 'cm') {
        return answerValue.cm && answerValue.cm > 0;
      }
      return answerValue.feet && answerValue.feet > 0;
    }
    if (question.type === 'country_select') {
      return answerValue && answerValue.length > 0;
    }
    if (question.type === 'multi_select') {
      // Coerce string (legacy comma-joined) to array, then require ≥1 selection.
      if (Array.isArray(answerValue)) return answerValue.length > 0;
      if (typeof answerValue === 'string' && answerValue.length > 0) {
        return answerValue.split(',').map(s => s.trim()).filter(Boolean).length > 0;
      }
      return false;
    }
    return true;
  };

  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  
  const handleSubmit = async (isRetry = false) => {
    // If user is NOT logged in, show the gate screen
    if (!user) {
      // Save all answers to localStorage for post-auth submission
      const guestData = {
        answers: getFormattedAnswers(),
        rawAnswers: answers,
        medications: medications,
        currentSupplements: currentSupplements,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(PENDING_GUEST_QUIZ_KEY, JSON.stringify(guestData));
      localStorage.setItem(SAVED_ASSESSMENT_KEY, JSON.stringify({
        answers,
        medications,
        currentSupplements,
        currentStep,
        savedAt: new Date().toISOString()
      }));
      setShowGateScreen(true);
      return;
    }

    setIsSubmitting(true);
    setSubmissionFailed(false);
    setProcessingStatus({ progress: 5, message: 'Starting analysis...' });
    
    if (!isRetry) {
      setSubmitAttempts(0);
    }
    
    try {
      // Start async assessment
      const startResponse = await axios.post(
        `${API}/assessment/start`,
        { 
          answers: getFormattedAnswers(),
          medications: medications,
          current_supplements: currentSupplements
        },
        { 
          headers: getAuthHeader(),
          timeout: 30000 // 30 second timeout just to start
        }
      );
      
      const assessmentId = startResponse.data.assessment_id;
      setProcessingStatus({ progress: 10, message: 'Analyzing your health profile...' });
      
      // Poll for completion
      const maxPolls = 60; // 60 polls * 3 seconds = 3 minutes max
      for (let poll = 0; poll < maxPolls; poll++) {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds
        
        try {
          const statusResponse = await axios.get(
            `${API}/assessment/status/${assessmentId}`,
            { headers: getAuthHeader(), timeout: 10000 }
          );
          
          const { status, progress, message, result } = statusResponse.data;
          setProcessingStatus({ progress, message });
          
          if (status === 'completed' && result) {
            localStorage.removeItem(SAVED_ASSESSMENT_KEY);
            toast.success('Assessment complete!');
            // After a retake, show the "What Changed" diff first
            if (isRevising) {
              navigate(`/what-changed/${result.id}`);
            } else {
              navigate(`/results/${result.id}`);
            }
            return;
          }
          
          if (status === 'failed') {
            throw new Error(message || 'Analysis failed');
          }
        } catch (pollError) {
          // If it's a 404, the assessment might still be processing
          if (pollError.response?.status !== 404) {
            console.error('Poll error:', pollError);
          }
        }
      }
      
      // Timeout after max polls
      throw new Error('Analysis is taking longer than expected. Please try again.');
      
    } catch (error) {
      console.error('Assessment submission failed:', error);
      setSubmitAttempts(prev => prev + 1);
      setSubmissionFailed(true);
      saveAssessmentLocally();
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Your assessment has been saved. Please log in again.');
        navigate('/auth?mode=login&redirect=/quiz');
      } else if (!navigator.onLine) {
        toast.error('No internet connection. Your assessment has been saved for later.');
      } else {
        toast.error(error.message || 'Failed to submit. Your assessment has been saved - try again later.');
      }
    } finally {
      setIsSubmitting(false);
      setProcessingStatus(null);
    }
  };

  if (loadingQuestions) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Show saved assessment prompt
  if (showSavedPrompt && hasSavedAssessment) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-lg border border-amber-200"
          data-testid="saved-quiz-resume-card"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Save className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-[#1A1A1A]">
                Pick up where you left off?
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {savedMeta
                  ? `${savedMeta.answered} answer${savedMeta.answered !== 1 ? 's' : ''} saved · ${savedMeta.agoLabel}`
                  : 'You have a previously saved assessment.'}
              </p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
            We&apos;ll restore your answers, medications, and supplements so you can finish the
            quiz from question {Math.max(1, (savedMeta?.currentStep || 0) + 1)}.
          </p>
          <div className="space-y-2.5">
            <Button
              onClick={restoreSavedAssessment}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="restore-assessment-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resume assessment
            </Button>
            <Button
              onClick={clearSavedAssessment}
              variant="outline"
              className="w-full"
              data-testid="start-fresh-btn"
            >
              Start fresh
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Gate Screen - shown when guest completes quiz without being logged in
  if (showGateScreen) {
    const healthCategories = [
      { name: 'Energy', icon: Zap, color: 'from-amber-400 to-orange-400' },
      { name: 'Sleep', icon: Moon, color: 'from-indigo-400 to-purple-400' },
      { name: 'Immunity', icon: Shield, color: 'from-green-400 to-emerald-400' },
      { name: 'Stress', icon: Brain, color: 'from-rose-400 to-pink-400' },
      { name: 'Digestion', icon: Activity, color: 'from-teal-400 to-cyan-400' },
      { name: 'Heart', icon: Heart, color: 'from-red-400 to-rose-400' },
      { name: 'Weight', icon: Scale, color: 'from-blue-400 to-indigo-400' },
      { name: 'Mood', icon: Smile, color: 'from-yellow-400 to-amber-400' },
      { name: 'Vision', icon: Eye, color: 'from-sky-400 to-blue-400' },
      { name: 'Joints', icon: Bone, color: 'from-orange-400 to-amber-400' },
    ];
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg w-full"
          data-testid="assessment-gate-screen"
        >
          {/* Success indicator */}
          <div className="text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"
            >
              <Check className="w-8 h-8 text-emerald-600" />
            </motion.div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">
              Your Report Is Ready!
            </h2>
            <p className="text-muted-foreground">
              Create a free account to unlock your personalized results
            </p>
          </div>

          {/* Blurred teaser of health categories */}
          <div className="bg-white rounded-2xl border border-border p-5 mb-4">
            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-muted-foreground" />
              Your Health Scores Preview
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {healthCategories.map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <motion.div
                    key={cat.name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.05 }}
                    className="flex items-center gap-2 p-2 rounded-lg bg-gray-50"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs font-medium text-[#1A1A1A]">{cat.name}</span>
                    <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden ml-auto">
                      <div className={`h-full rounded-full bg-gradient-to-r ${cat.color} blur-[2px]`} style={{ width: `${40 + Math.random() * 50}%` }} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div className="mt-3 p-2 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-xs text-primary text-center font-medium">
                + Personalized supplement plan with dosages, timing & safety checks
              </p>
            </div>
          </div>

          {/* Auth buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/auth?mode=register&redirect=/quiz')}
              className="w-full btn-primary py-3 text-base"
              data-testid="gate-signup-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create Free Account & View Results
            </Button>
            <Button
              onClick={() => navigate('/auth?mode=login&redirect=/quiz')}
              variant="outline"
              className="w-full py-3"
              data-testid="gate-login-btn"
            >
              Already have an account? Sign In
            </Button>
          </div>

          <p className="text-[10px] text-muted-foreground text-center mt-4">
            Your answers are saved and will be processed instantly after sign-up. No need to redo the quiz.
          </p>
        </motion.div>
      </div>
    );
  }

  if (!questions.length) {
    return null;
  }

  const currentQuestion = !isSupplementStep && !isMedicationStep ? filteredQuestions[currentStep] : null;
  const isCurrentAnswered = isSupplementStep || isMedicationStep || 
    (currentQuestion && answers[currentQuestion.id] && checkIfAnswered(currentQuestion, answers[currentQuestion.id]));

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#1A1A1A]"
          >
            <Logo size="default" linkTo={null} className="pointer-events-none" />
          </button>
          <div className="flex items-center gap-4">
            {/* Time estimate */}
            <div className="hidden md:flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{estimatedTimeRemaining}</span>
            </div>
            {/* Emergency Exit Button */}
            <a 
              href="https://en.wikipedia.org/wiki/List_of_emergency_telephone_numbers"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-full transition-colors"
              data-testid="emergency-exit-button"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">Emergency?</span>
            </a>
          </div>
        </div>
        
        {/* Enhanced Progress Section */}
        <div className="max-w-4xl mx-auto px-6 pb-4">
          {/* Section pills - scrollable on mobile */}
          <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-2 scrollbar-hide">
            {quizSections.slice(0, 8).map((section, index) => {
              const sectionProgress = getSectionProgress();
              const isCompleted = index < sectionProgress.currentSectionIndex;
              const isCurrent = section === currentSection;
              const isUpcoming = index > sectionProgress.currentSectionIndex;
              
              return (
                <div
                  key={section}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    isCompleted 
                      ? 'bg-green-100 text-green-700' 
                      : isCurrent 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted && <Check className="w-3 h-3" />}
                  {formatSectionName(section)}
                </div>
              );
            })}
            {quizSections.length > 8 && (
              <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
                +{quizSections.length - 8} more
              </div>
            )}
          </div>
          
          {/* Progress bar with percentage */}
          <div className="relative">
            <Progress value={actualProgress} className="h-2 rounded-full" />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-muted-foreground">
                Question {currentStep + 1} of {totalSteps}
              </span>
              <span className="text-xs font-medium text-primary">
                {Math.round(actualProgress)}% complete
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Quiz Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {!isSupplementStep && !isMedicationStep ? (
            // Regular question step
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            >
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 capitalize">
                {currentQuestion.category.replace('_', ' ')}
              </span>

              {/* Smart Retake: Skip-to-weight button on demographic Q's when prefilled & unchanged */}
              {isRevising && originalAnswers && currentQuestion && (() => {
                const STATIC_IDS = ['age', 'gender', 'country', 'ancestry', 'activity_level'];
                if (!STATIC_IDS.includes(currentQuestion.id)) return null;
                // Find the index of the first non-static question (height/weight onwards)
                const skipToIdx = filteredQuestions.findIndex(q => !STATIC_IDS.includes(q.id));
                const skipTo = filteredQuestions[skipToIdx];
                const skipLabel = skipTo ? `Skip to question ${skipToIdx + 1}` : 'Skip ahead';
                return (
                  <div className="mb-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 space-y-2" data-testid="smart-retake-skip">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 text-xs text-emerald-800 leading-relaxed">
                        Just want to update one thing? Use{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/edit-answers')}
                          className="underline font-semibold text-emerald-700 hover:text-emerald-900"
                          data-testid="smart-retake-edit-link"
                        >
                          Edit specific answers
                        </button>{' '}
                        instead — no need to scroll through all 34 questions.
                      </div>
                    </div>
                    {skipToIdx > currentStep && (
                      <div className="flex items-center gap-2 pt-1.5 border-t border-emerald-200/70">
                        <div className="flex-1 text-[11px] text-emerald-700">
                          These basics rarely change — jump ahead to weight, conditions, lifestyle.
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(skipToIdx)}
                          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline whitespace-nowrap"
                          data-testid="smart-retake-skip-btn"
                        >
                          {skipLabel} →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {isRevising && originalAnswers && currentQuestion && (() => {
                const orig = originalAnswers[currentQuestion.id];
                const curr = answers[currentQuestion.id];
                const changed = orig !== undefined && JSON.stringify(orig) !== JSON.stringify(curr);
                if (changed) {
                  return (
                    <span className="inline-block ml-2 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                      Edited
                    </span>
                  );
                }
                return null;
              })()}

              <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-2">
                {currentQuestion.question}
              </h2>
              {currentQuestion.helper_text && (
                <p className="text-sm text-red-600 mb-6 italic" data-testid="question-helper-text">
                  {currentQuestion.helper_text}
                </p>
              )}
              {!currentQuestion.helper_text && <div className="mb-8" />}

              {/* Weight Input - Auto-advances after entering weight */}
              {currentQuestion.type === 'weight_input' ? (
                <div className="space-y-4">
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-2">Weight</label>
                      <Input
                        type="number"
                        placeholder="Enter weight"
                        autoFocus
                        value={answers[currentQuestion.id]?.value || ''}
                        onChange={(e) => {
                          handleAnswer(currentQuestion.id, {
                            value: e.target.value,
                            unit: answers[currentQuestion.id]?.unit || 'lbs'
                          });
                          // Auto-advance when a reasonable weight value is entered (3 digits like 150)
                          if (e.target.value && e.target.value.length >= 2) {
                            setTimeout(() => {
                              const currentValue = document.querySelector('[data-testid="weight-input"]')?.value;
                              if (currentValue && currentValue === e.target.value && parseFloat(e.target.value) > 0) {
                                if (currentStep < totalSteps - 1) {
                                  setCurrentStep(currentStep + 1);
                                }
                              }
                            }, 800);
                          }
                        }}
                        onBlur={(e) => {
                          // Also advance on blur if value is valid
                          if (e.target.value && parseFloat(e.target.value) > 0) {
                            setTimeout(() => {
                              if (currentStep < totalSteps - 1) {
                                setCurrentStep(currentStep + 1);
                              }
                            }, 300);
                          }
                        }}
                        data-testid="weight-input"
                        className="text-lg"
                        min="1"
                        max="1000"
                      />
                    </div>
                    <div className="w-32">
                      <label className="block text-sm font-medium mb-2">Unit</label>
                      <select
                        value={answers[currentQuestion.id]?.unit || 'lbs'}
                        onChange={(e) => handleAnswer(currentQuestion.id, {
                          value: answers[currentQuestion.id]?.value || '',
                          unit: e.target.value
                        })}
                        data-testid="weight-unit"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="lbs">lbs</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>
                  {answers[currentQuestion.id]?.value && (
                    <p className="text-sm text-muted-foreground">
                      {answers[currentQuestion.id]?.unit === 'kg' 
                        ? `≈ ${Math.round(answers[currentQuestion.id].value * 2.205)} lbs`
                        : `≈ ${Math.round(answers[currentQuestion.id].value / 2.205)} kg`
                      }
                    </p>
                  )}
                </div>
              ) : currentQuestion.type === 'country_select' ? (
                /* Country Select - Searchable with alphabetical list */
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      placeholder="Type to search countries..."
                      className="w-full h-12 px-4 rounded-xl border-2 border-border bg-white text-base focus:border-primary focus:outline-none"
                      data-testid="country-search"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-xl border border-border bg-white">
                    {(() => {
                      const countries = [
                        "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
                        "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
                        "Cambodia", "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
                        "Denmark", "Djibouti", "Dominica", "Dominican Republic",
                        "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
                        "Fiji", "Finland", "France",
                        "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
                        "Haiti", "Honduras", "Hong Kong", "Hungary",
                        "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
                        "Jamaica", "Japan", "Jordan",
                        "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
                        "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
                        "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
                        "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
                        "Oman",
                        "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
                        "Qatar",
                        "Romania", "Russia", "Rwanda",
                        "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
                        "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
                        "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
                        "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
                        "Yemen",
                        "Zambia", "Zimbabwe",
                        "Other"
                      ];
                      
                      const filteredCountries = countries.filter(c => 
                        c.toLowerCase().includes((countrySearch || '').toLowerCase())
                      );
                      
                      return filteredCountries.map((country) => (
                        <button
                          key={country}
                          onClick={async () => {
                            handleAnswer(currentQuestion.id, country);
                            setCountrySearch('');
                            // Use the BROWSER timezone (most accurate; works for multi-TZ countries
                            // like US/Canada/Russia/Australia without asking the user).
                            try {
                              const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                              if (browserTz && user) {
                                axios.post(`${API}/auth/timezone`, { timezone: browserTz }, { headers: getAuthHeader() }).catch(() => {});
                              } else {
                                // Fallback to country default if browser TZ isn't available
                                const res = await axios.get(`${API}/country-timezone/${encodeURIComponent(country)}`).catch(() => null);
                                if (res?.data?.timezone && user) {
                                  axios.post(`${API}/auth/timezone`, { timezone: res.data.timezone }, { headers: getAuthHeader() }).catch(() => {});
                                }
                              }
                            } catch (_) { /* non-blocking */ }
                            // Auto-advance after country selection
                            setTimeout(() => {
                              if (currentStep < totalSteps - 1) {
                                setCurrentStep(currentStep + 1);
                              }
                            }, 300);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-colors border-b border-border/50 last:border-b-0 ${
                            answers[currentQuestion.id] === country ? 'bg-primary/10 text-primary font-medium' : ''
                          }`}
                        >
                          {country}
                        </button>
                      ));
                    })()}
                  </div>
                  {answers[currentQuestion.id] && (
                    <p className="text-sm text-primary font-medium">
                      Selected: {answers[currentQuestion.id]}
                    </p>
                  )}
                </div>
              ) : currentQuestion.type === 'multi_select' ? (
                /* Multi-Select (checkboxes) - Auto-advances only when "None" or "Not applicable" is selected */
                <div className="space-y-3">
                  {(() => {
                    // Defensive dedup: even if a question definition slipped a duplicate
                    // option into the array, only render each unique value once.
                    const seen = new Set();
                    return (currentQuestion.options || []).filter(opt => {
                      const k = String(opt);
                      if (seen.has(k)) return false;
                      seen.add(k);
                      return true;
                    });
                  })().map((option, index) => {
                    // Robustly coerce prefilled answers (stored as comma-joined strings
                    // in old assessments) into arrays so checkboxes work in retake mode.
                    const rawSelected = answers[currentQuestion.id];
                    const selectedOptions = Array.isArray(rawSelected)
                      ? rawSelected
                      : (typeof rawSelected === 'string' && rawSelected.length > 0)
                        ? rawSelected.split(',').map(s => s.trim()).filter(Boolean)
                        : [];
                    const isSelected = selectedOptions.some(
                      s => s === option || (typeof s === 'string' && typeof option === 'string' && s.toLowerCase() === option.toLowerCase())
                    );
                    const isNoneOrNA = option === 'None' || option === 'Not applicable';
                    
                    return (
                      <motion.button
                        key={option}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => {
                          let newSelection;
                          if (isNoneOrNA) {
                            // If clicking "None" or "Not applicable", clear all other selections
                            newSelection = isSelected ? [] : [option];
                            handleAnswer(currentQuestion.id, newSelection);
                            // Auto-advance only when selecting "None" or "Not applicable"
                            if (!isSelected) {
                              setTimeout(() => {
                                if (currentStep < totalSteps - 1) {
                                  setCurrentStep(currentStep + 1);
                                }
                              }, 400);
                            }
                          } else {
                            // Remove "None" or "Not applicable" if selecting something else
                            const withoutNoneOrNA = (selectedOptions || []).filter(o => o !== 'None' && o !== 'Not applicable');
                            if (isSelected) {
                              newSelection = withoutNoneOrNA.filter(o => o !== option);
                            } else {
                              newSelection = [...withoutNoneOrNA, option];
                            }
                            handleAnswer(currentQuestion.id, newSelection);
                          }
                        }}
                        data-testid={`quiz-option-${currentQuestion.id}-${index}`}
                        className={`w-full p-4 min-h-[56px] text-left rounded-xl border-2 transition-all duration-300 touch-manipulation active:scale-[0.98] ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border bg-white hover:border-primary/30 hover:bg-primary/[0.02]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                            isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <span className={`text-base ${isSelected ? 'text-primary font-medium' : 'text-[#1A1A1A]'}`}>
                            {option}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                  {(() => {
                    const raw = answers[currentQuestion.id];
                    const arr = Array.isArray(raw)
                      ? raw
                      : (typeof raw === 'string' && raw.length > 0)
                        ? raw.split(',').map(s => s.trim()).filter(Boolean)
                        : [];
                    if (arr.length === 0) return null;
                    if (arr.includes('None') || arr.includes('Not applicable')) return null;
                    return (
                      <p className="text-sm text-muted-foreground mt-2">
                        {arr.length} selected
                      </p>
                    );
                  })()}
                </div>
              ) : currentQuestion.type === 'height_input' ? (
                /* Height Input - Auto-jumps from feet to inches, then advances to next question */
                <div className="space-y-4">
                  <div className="flex gap-4 items-end">
                    {(answers[currentQuestion.id]?.unit || 'ft-in') === 'ft-in' ? (
                      <>
                        <div className="flex-1">
                          <label className="block text-sm font-medium mb-2">Feet</label>
                          <Input
                            type="number"
                            placeholder="Feet"
                            autoFocus
                            value={answers[currentQuestion.id]?.feet || ''}
                            onChange={(e) => {
                              handleAnswer(currentQuestion.id, {
                                ...answers[currentQuestion.id],
                                feet: e.target.value,
                                unit: 'ft-in'
                              });
                              // Auto-jump to inches field when feet has a valid value (1 digit)
                              if (e.target.value && e.target.value.length >= 1) {
                                setTimeout(() => {
                                  const inchesInput = document.querySelector('[data-testid="height-inches"]');
                                  if (inchesInput) inchesInput.focus();
                                }, 100);
                              }
                            }}
                            data-testid="height-feet"
                            className="text-lg"
                            min="1"
                            max="8"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium mb-2">Inches</label>
                          <Input
                            type="number"
                            placeholder="Inches"
                            value={answers[currentQuestion.id]?.inches || ''}
                            onChange={(e) => {
                              handleAnswer(currentQuestion.id, {
                                ...answers[currentQuestion.id],
                                inches: e.target.value,
                                unit: 'ft-in'
                              });
                              // Auto-advance when both feet and inches are filled (2 digits for inches like 10, 11)
                              const feet = answers[currentQuestion.id]?.feet;
                              if (feet && e.target.value && (e.target.value.length >= 2 || parseInt(e.target.value) <= 9)) {
                                // Wait a moment to see if user is typing more
                                setTimeout(() => {
                                  const currentInches = document.querySelector('[data-testid="height-inches"]')?.value;
                                  if (currentInches && currentInches === e.target.value) {
                                    if (currentStep < totalSteps - 1) {
                                      setCurrentStep(currentStep + 1);
                                    }
                                  }
                                }, 800);
                              }
                            }}
                            onBlur={(e) => {
                              // Also advance on blur if values are valid
                              const feet = answers[currentQuestion.id]?.feet;
                              if (feet && parseFloat(feet) > 0 && e.target.value) {
                                setTimeout(() => {
                                  if (currentStep < totalSteps - 1) {
                                    setCurrentStep(currentStep + 1);
                                  }
                                }, 300);
                              }
                            }}
                            data-testid="height-inches"
                            className="text-lg"
                            min="0"
                            max="11"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="flex-1">
                        <label className="block text-sm font-medium mb-2">Centimeters</label>
                        <Input
                          type="number"
                          placeholder="cm"
                          autoFocus
                          value={answers[currentQuestion.id]?.cm || ''}
                          onChange={(e) => handleAnswer(currentQuestion.id, {
                            cm: e.target.value,
                            unit: 'cm'
                          })}
                          onBlur={(e) => {
                            // Auto-advance after cm input when value is valid
                            if (e.target.value && parseFloat(e.target.value) > 0) {
                              setTimeout(() => {
                                if (currentStep < totalSteps - 1) {
                                  setCurrentStep(currentStep + 1);
                                }
                              }, 300);
                            }
                          }}
                          data-testid="height-cm"
                          className="text-lg"
                          min="50"
                          max="300"
                        />
                      </div>
                    )}
                    <div className="w-32">
                      <label className="block text-sm font-medium mb-2">Unit</label>
                      <select
                        value={answers[currentQuestion.id]?.unit || 'ft-in'}
                        onChange={(e) => {
                          const newUnit = e.target.value;
                          const current = answers[currentQuestion.id] || {};
                          if (newUnit === 'cm' && current.feet) {
                            // Convert ft-in to cm
                            const totalInches = (parseInt(current.feet || 0) * 12) + parseInt(current.inches || 0);
                            handleAnswer(currentQuestion.id, {
                              cm: Math.round(totalInches * 2.54),
                              unit: 'cm'
                            });
                          } else if (newUnit === 'ft-in' && current.cm) {
                            // Convert cm to ft-in
                            const totalInches = Math.round(current.cm / 2.54);
                            handleAnswer(currentQuestion.id, {
                              feet: Math.floor(totalInches / 12),
                              inches: totalInches % 12,
                              unit: 'ft-in'
                            });
                          } else {
                            handleAnswer(currentQuestion.id, { ...current, unit: newUnit });
                          }
                        }}
                        data-testid="height-unit"
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="ft-in">ft / in</option>
                        <option value="cm">cm</option>
                      </select>
                    </div>
                  </div>
                  {answers[currentQuestion.id] && (
                    <p className="text-sm text-muted-foreground">
                      {answers[currentQuestion.id]?.unit === 'cm' && answers[currentQuestion.id]?.cm
                        ? `≈ ${Math.floor(answers[currentQuestion.id].cm / 2.54 / 12)}' ${Math.round(answers[currentQuestion.id].cm / 2.54 % 12)}"`
                        : answers[currentQuestion.id]?.feet 
                        ? `≈ ${Math.round(((parseInt(answers[currentQuestion.id].feet || 0) * 12) + parseInt(answers[currentQuestion.id].inches || 0)) * 2.54)} cm`
                        : ''
                      }
                    </p>
                  )}
                </div>
              ) : (
                /* Standard Options */
                <div className="space-y-3">
                  {currentQuestion.options.map((option, index) => {
                    const storedAnswer = answers[currentQuestion.id];
                    const isSelected = storedAnswer === option || 
                      (typeof storedAnswer === 'string' && typeof option === 'string' && storedAnswer.toLowerCase() === option.toLowerCase());
                    return (
                      <motion.button
                        key={option}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => handleOptionSelect(option)}
                        data-testid={`quiz-option-${currentQuestion.id}-${index}`}
                        className={`w-full p-5 min-h-[60px] text-left rounded-xl border-2 transition-all duration-300 touch-manipulation active:scale-[0.98] ${
                          isSelected 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border bg-white hover:border-primary/30 hover:bg-primary/[0.02]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-base ${isSelected ? 'text-primary font-medium' : 'text-[#1A1A1A]'}`}>
                            {option}
                          </span>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          ) : isSupplementStep ? (
            // Supplements step - what they're currently taking
            <motion.div
              key="supplements"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Current Supplements
              </span>

              <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">
                Are you currently taking any supplements?
              </h2>
              
              <p className="text-muted-foreground mb-8">
                Tell us what you're already taking so we can avoid recommending duplicates and check for interactions.
                <span className="text-primary font-medium"> We'll tailor your plan accordingly.</span>
              </p>

              {/* Supplement input */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search or type a supplement..."
                  value={supplementInput}
                  onChange={(e) => setSupplementInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomSupplement();
                    }
                  }}
                  className="pl-10 h-12 bg-white border-border focus:border-primary"
                  data-testid="supplement-input"
                />
                {supplementInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCustomSupplement}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    Add
                  </Button>
                )}
                
                {/* Dropdown */}
                {showSupplementDropdown && filteredSupplements.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    {filteredSupplements.map((supp, i) => (
                      <button
                        key={i}
                        onClick={() => addSupplement(supp.name)}
                        className="w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center justify-between border-b border-border/50 last:border-0"
                      >
                        <span className="text-[#1A1A1A]">{supp.name}</span>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          {supp.category}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected supplements */}
              {currentSupplements.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#1A1A1A] mb-3">Your current supplements:</p>
                  <div className="flex flex-wrap gap-2">
                    {currentSupplements.map((supp) => (
                      <motion.div
                        key={supp}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span className="text-sm font-medium">{supp}</span>
                        <button 
                          onClick={() => removeSupplement(supp)}
                          className="hover:bg-green-200 rounded-full p-0.5"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Common supplements quick add */}
              <div className="mt-8">
                <p className="text-sm text-muted-foreground mb-3">Popular supplements (click to add):</p>
                <div className="flex flex-wrap gap-2">
                  {(commonSupplements.filter(s => s.popular).length > 0
                    ? commonSupplements.filter(s => s.popular)
                    : commonSupplements
                  ).slice(0, 12).map((supp, i) => (
                    <button
                      key={i}
                      onClick={() => addSupplement(supp.name)}
                      disabled={currentSupplements.includes(supp.name)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        currentSupplements.includes(supp.name)
                          ? 'bg-muted text-muted-foreground border-muted cursor-not-allowed'
                          : 'bg-white border-border hover:border-primary/30 text-[#1A1A1A]'
                      }`}
                    >
                      {supp.name}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-6">
                Not taking any supplements? No problem! Just click "Next" to continue.
              </p>
            </motion.div>
          ) : (
            // Medication step
            <motion.div
              key="medications"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/20 text-secondary text-sm font-medium mb-6">
                <Pill className="w-4 h-4" />
                Medication Review
              </span>

              <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">
                Are you currently taking any medications?
              </h2>
              
              <p className="text-muted-foreground mb-8">
                This helps us check for potential medication-supplement interactions and ensure your safety. 
                <span className="text-primary font-medium"> Your information is kept private.</span>
              </p>

              {/* Warning box */}
              <div className="flex items-start gap-3 p-4 mb-8 rounded-xl bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Why we ask about medications</p>
                  <p className="text-amber-700">Some supplements can interact with medications, reducing their effectiveness or causing side effects. We'll analyze your medications and adjust recommendations accordingly.</p>
                </div>
              </div>

              {/* Medication input */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search or type a medication..."
                  value={medicationInput}
                  onChange={(e) => setMedicationInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomMedication();
                    }
                  }}
                  className="pl-10 h-12 bg-white border-border focus:border-primary"
                  data-testid="medication-input"
                />
                {medicationInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCustomMedication}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    Add
                  </Button>
                )}
                
                {/* Dropdown */}
                {showMedicationDropdown && filteredMedications.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    {filteredMedications.map((med, i) => (
                      <button
                        key={i}
                        onClick={() => addMedication(med.name)}
                        className="w-full px-4 py-3 text-left hover:bg-primary/5 flex items-center justify-between border-b border-border/50 last:border-0"
                      >
                        <span className="text-[#1A1A1A]">{med.name}</span>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          {med.category}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected medications */}
              {medications.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#1A1A1A] mb-3">Your medications:</p>
                  <div className="flex flex-wrap gap-2">
                    {medications.map((med, i) => (
                      <motion.div
                        key={med}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full"
                      >
                        <Pill className="w-4 h-4" />
                        <span className="text-sm font-medium">{med}</span>
                        <button 
                          onClick={() => removeMedication(med)}
                          className="hover:bg-primary/20 rounded-full p-0.5"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Common medications quick add */}
              <div className="mt-8">
                <p className="text-sm text-muted-foreground mb-3">Common medications (click to add):</p>
                <div className="flex flex-wrap gap-2">
                  {commonMedications.slice(0, 10).map((med, i) => (
                    <button
                      key={i}
                      onClick={() => addMedication(med.name)}
                      disabled={medications.includes(med.name)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        medications.includes(med.name)
                          ? 'bg-muted text-muted-foreground border-muted cursor-not-allowed'
                          : 'bg-white border-border hover:border-primary/30 text-[#1A1A1A]'
                      }`}
                    >
                      {med.name}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-6">
                Don't take any medications? No problem! Just click "Get My Results" to continue.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {isMedicationStep ? (
            <div className="flex flex-col sm:flex-row gap-2">
              {submissionFailed && (
                <Button
                  onClick={saveAssessmentLocally}
                  variant="outline"
                  className="text-muted-foreground"
                  data-testid="save-assessment-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save for Later
                </Button>
              )}
              <Button
                onClick={() => handleSubmit(submissionFailed)}
                disabled={isSubmitting}
                data-testid="submit-assessment"
                className="btn-primary min-w-[180px]"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">
                      {processingStatus?.message || 'Analyzing...'}
                    </span>
                  </div>
                ) : submissionFailed ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </>
                ) : (
                  <>
                    Get My Results
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!isCurrentAnswered}
              className="btn-primary"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
